import React, { useState, useEffect, useRef } from "react";
import { ArrowRight, Mail, Lock, Eye, EyeOff } from "lucide-react";
import { GOLD } from "../constants/theme";
import { BAM_LOGO_PNG } from "../constants/branding";
import { supabase } from "../../supabaseClient";
import { logEvent, FunnelEvent } from "../utils/analytics";

/**
 * QuickSignup — the low-friction 1-screen signup before payment.
 *
 * Flow:
 *  1. Coach enters email + password
 *  2. supabase.auth.signUp() creates the auth user
 *  3. A minimal profiles row is upserted with { id } so downstream selects don't error
 *  4. Parent is notified via onSuccess(user) — BAMFull flips into authenticated state
 *     and the next render lands on the Paywall (since stripe_subscription_active is false)
 *
 * Profile details (name, country, roles, etc.) are collected AFTER payment
 * via a "Complete your profile" banner on the dashboard. This is intentional —
 * every form field before a paywall costs us conversions.
 */
export function QuickSignup({ onSuccess, onSignInClick }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Set when Supabase returns a user but NO session — i.e. email confirmation
  // is required. Without this the user is treated as signed-in but isn't, and
  // every authed query silently fails.
  const [verifySentTo, setVerifySentTo] = useState(null);
  // Explicit affirmative consent. We previously relied on a "by continuing
  // you agree…" footer (implicit consent), which is technically valid in
  // most US jurisdictions but weaker. A checkbox tied to the submit button
  // gives us a defensible audit trail if someone disputes acceptance later.
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const emailRef = useRef(null);

  useEffect(() => {
    logEvent(FunnelEvent.SignupStarted);
    setTimeout(() => emailRef.current?.focus(), 100);
  }, []);

  const validate = () => {
    if (!email.trim()) return "Please enter your email";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) return "Please enter a valid email";
    if (password.length < 8) return "Password must be at least 8 characters";
    if (!agreedToTerms) return "Please agree to the Terms and Privacy Policy to continue";
    return null;
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    const err = validate();
    if (err) { setError(err); return; }
    setError(null);
    setLoading(true);
    try {
      // 1. Sign up with Supabase auth
      const { data, error: authError } = await supabase.auth.signUp({
        email: email.trim().toLowerCase(),
        password,
      });
      if (authError) throw authError;
      const user = data.user;
 if (!user) throw new Error("Signup failed: no user returned");

      // If there's no session, Supabase requires email confirmation. Don't
      // pretend they're signed in (that lands them on a broken paywall with a
      // null session) — tell them to check their inbox.
      if (!data.session) {
        try { localStorage.setItem("bam_just_signed_up", "1"); } catch {}
        logEvent(FunnelEvent.SignupCompleted, { needsVerify: true });
        setVerifySentTo(email.trim().toLowerCase());
        setLoading(false);
        return;
      }

      // 2. Create a minimal profile row so the subscription check has something to read.
      //    The backend webhook will populate stripe_* fields after payment.
      //    We use upsert so this is safe if a trigger already created the row.
      try {
        await supabase.from("profiles").upsert({ id: user.id }, { onConflict: "id" });
      } catch (profileErr) {
        // Non-fatal — if this fails, the subscription check will still work
        // (it'll just see no row, treat it as not subscribed, and show paywall)
        console.warn("[QuickSignup] profile upsert failed (non-fatal):", profileErr);
      }

      // 3. Mark signup success
      try { localStorage.setItem("bam_auth_token", "sb"); localStorage.setItem("bam_just_signed_up", "1"); } catch {}
      // Record first-touch affiliate attribution if they arrived via ?ref=<code>.
      try { const { recordReferral } = await import("../utils/affiliateRef"); await recordReferral(); } catch {}
      logEvent(FunnelEvent.SignupCompleted);
      onSuccess?.(user);
    } catch (e) {
      console.error("[QuickSignup] error:", e);
      // Friendly error messages for common cases
      const msg = e.message || "Couldn't create your account. Please try again.";
      if (msg.toLowerCase().includes("already") || msg.toLowerCase().includes("registered")) {
        setError("An account with this email already exists. Try signing in instead.");
      } else if (msg.toLowerCase().includes("password")) {
        setError("Password doesn't meet requirements. Try at least 8 characters.");
      } else {
        setError(msg);
      }
      setLoading(false);
    }
  };

  if (verifySentTo) {
    return (
      <div style={{ position:"fixed", inset:0, background:"#050505", color:"#fff", fontFamily:"'DM Sans', sans-serif", display:"flex", alignItems:"center", justifyContent:"center", padding:"24px", paddingTop:"calc(24px + env(safe-area-inset-top,0px))", textAlign:"center" }}>
        <div style={{ maxWidth:380 }}>
          <div style={{ fontSize:42, marginBottom:16 }}>📬</div>
          <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:28, letterSpacing:1.5, color:"#E2DD9F", marginBottom:12 }}>CHECK YOUR EMAIL</div>
          <div style={{ fontSize:14, color:"#bbb", lineHeight:1.6, marginBottom:24 }}>
            We sent a confirmation link to <span style={{ color:"#E2DD9F", fontWeight:600 }}>{verifySentTo}</span>. Tap it to verify your account, then come back and sign in. Check your spam folder if it's not there in a minute.
          </div>
          <button onClick={() => (onSignInClick ? onSignInClick() : setVerifySentTo(null))}
            style={{ padding:"13px 30px", borderRadius:10, background:"#E2DD9F", color:"#111", border:"none", fontFamily:"'Bebas Neue',sans-serif", fontSize:16, letterSpacing:1.4, cursor:"pointer" }}>
            GO TO SIGN IN
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "#050505",
        color: "#fff",
        fontFamily: "'DM Sans', sans-serif",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
        overflow: "auto",
      }}
    >
      {/* Ambient glow */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          background: `radial-gradient(ellipse 800px 500px at 50% 30%, rgba(226,221,159,0.08), transparent 70%)`,
          pointerEvents: "none",
        }}
      />

      <div
        style={{
          position: "relative",
          width: "100%",
          maxWidth: 440,
          background: "linear-gradient(180deg, #131313 0%, #0E0E0E 100%)",
          border: `1px solid rgba(226,221,159,0.18)`,
          borderRadius: 20,
          padding: "44px 36px 36px",
          boxShadow: "0 40px 120px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.02), inset 0 1px 0 rgba(255,255,255,0.03)",
          textAlign: "center",
        }}
      >
        {/* Logo */}
        <img
          src={BAM_LOGO_PNG}
          alt="BAM"
          style={{
            width: 48,
            height: 48,
            objectFit: "contain",
            margin: "0 auto 18px",
            display: "block",
            filter: "drop-shadow(0 0 24px rgba(226,221,159,0.25))",
          }}
        />

        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            color: GOLD,
            letterSpacing: 1.5,
            textTransform: "uppercase",
            marginBottom: 8,
            opacity: 0.85,
          }}
        >
          Create your account
        </div>

        <h1
          style={{
            fontFamily: "'Bebas Neue', sans-serif",
            fontSize: 38,
            letterSpacing: 2,
            margin: "0 0 10px",
            lineHeight: 1.05,
            color: "#fff",
          }}
        >
          JOIN THE PLATFORM
        </h1>

        <p
          style={{
            color: "#9a9a9a",
            fontSize: 13,
            margin: "0 0 26px",
            lineHeight: 1.5,
          }}
        >
          Takes 30 seconds. You can complete your coaching profile after.
        </p>

        {/* noValidate disables HTML5's default tooltip popups so our own
            inline error rendering (with role="alert") is the single source
            of validation feedback for users on assistive tech. */}
        <form onSubmit={handleSubmit} noValidate style={{ textAlign: "left" }}>
          {/* Email */}
          <label htmlFor="signup-email" style={labelStyle}>Email</label>
          <div style={{ position: "relative", marginBottom: 14 }}>
            <Mail size={15} color="#666" aria-hidden="true" style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)" }} />
            <input
              ref={emailRef}
              id="signup-email"
              type="email"
              autoComplete="email"
              required
              aria-required="true"
              aria-invalid={!!error && error.toLowerCase().includes("email")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              style={{ ...inputStyle, paddingLeft: 38 }}
            />
          </div>

          {/* Password */}
          <label htmlFor="signup-password" style={labelStyle}>Password</label>
          <div style={{ position: "relative", marginBottom: 4 }}>
            <Lock size={15} color="#666" aria-hidden="true" style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)" }} />
            <input
              id="signup-password"
              type={showPassword ? "text" : "password"}
              autoComplete="new-password"
              required
              aria-required="true"
              aria-describedby="signup-password-hint"
              aria-invalid={!!error && error.toLowerCase().includes("password")}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 8 characters"
              style={{ ...inputStyle, paddingLeft: 38, paddingRight: 40 }}
            />
            <button
              type="button"
              onClick={() => setShowPassword((s) => !s)}
              aria-label={showPassword ? "Hide password" : "Show password"}
              aria-pressed={showPassword}
              style={{
                position: "absolute",
                right: 8,
                top: "50%",
                transform: "translateY(-50%)",
                background: "transparent",
                border: "none",
                color: "#666",
                cursor: "pointer",
                padding: 6,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {showPassword ? <EyeOff size={14} aria-hidden="true" /> : <Eye size={14} aria-hidden="true" />}
            </button>
          </div>
          <div id="signup-password-hint" style={{ fontSize: 11, color: "#666", marginBottom: 20, marginLeft: 2 }}>
            8+ characters, anything you'll remember
          </div>

          {/* Terms + Privacy explicit consent checkbox.
              The label uses the standard "I have read and agree" wording
              that's enforceable across most jurisdictions. The checkbox
              and the submit button are both required to proceed. */}
          <label
            htmlFor="agree-terms"
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 10,
              marginBottom: 16,
              padding: "10px 12px",
              background: agreedToTerms ? "rgba(226,221,159,0.06)" : "rgba(255,255,255,0.025)",
              border: `1px solid ${agreedToTerms ? "rgba(226,221,159,0.28)" : "rgba(255,255,255,0.08)"}`,
              borderRadius: 8,
              cursor: "pointer",
              transition: "all .15s ease",
              userSelect: "none",
            }}
          >
            <input
              id="agree-terms"
              type="checkbox"
              checked={agreedToTerms}
              onChange={(e) => setAgreedToTerms(e.target.checked)}
              required
              aria-required="true"
              aria-describedby="agree-terms-desc"
              aria-invalid={!!error && error.toLowerCase().includes("agree")}
              style={{
                marginTop: 2,
                width: 16,
                height: 16,
                accentColor: GOLD,
                cursor: "pointer",
                flexShrink: 0,
              }}
            />
            <span id="agree-terms-desc" style={{ fontSize: 11.5, color: "#bbb", lineHeight: 1.5 }}>
              I have read and agree to the{" "}
              <a
                href="#/terms"
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                style={{ color: GOLD, textDecoration: "none", borderBottom: `1px dotted ${GOLD}55` }}
              >
                Terms of Service
              </a>
              {" "}and{" "}
              <a
                href="#/privacy"
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                style={{ color: GOLD, textDecoration: "none", borderBottom: `1px dotted ${GOLD}55` }}
              >
                Privacy Policy
              </a>
              .
            </span>
          </label>

          {/* Error — role="alert" + aria-live="assertive" so screen readers
              interrupt and announce immediately on validation failure. */}
          {error && (
            <div
              id="signup-error"
              role="alert"
              aria-live="assertive"
              style={{
                marginBottom: 16,
                padding: "10px 13px",
                background: "rgba(224,96,96,0.08)",
                border: "1px solid rgba(224,96,96,0.25)",
                borderRadius: 8,
                fontSize: 12,
                color: "#ff9d9d",
                lineHeight: 1.5,
              }}
            >
              {error}
            </div>
          )}

          {/* Submit — disabled until checkbox checked. aria-describedby
              gives screen readers context for *why* it's disabled when
              they tab to it. */}
          <button
            type="submit"
            disabled={loading || !agreedToTerms}
            aria-describedby={!agreedToTerms ? "agree-terms-desc" : undefined}
            className="btn"
            style={{
              width: "100%",
              padding: "15px 24px",
              background: `linear-gradient(180deg, ${GOLD} 0%, #d4ce8a 100%)`,
              color: "#0A0A0A",
              border: "none",
              borderRadius: 10,
              fontSize: 14,
              fontWeight: 800,
              letterSpacing: 1.5,
              fontFamily: "'Bebas Neue', sans-serif",
              textTransform: "uppercase",
              cursor: (loading || !agreedToTerms) ? "not-allowed" : "pointer",
              opacity: loading ? 0.7 : (agreedToTerms ? 1 : 0.5),
              boxShadow: `0 10px 40px ${GOLD}28, 0 0 0 1px rgba(255,255,255,0.15) inset`,
              transition: "all .18s ease",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
            }}
          >
            {loading ? "Creating account..." : (
              <>
                Continue to payment <ArrowRight size={15} aria-hidden="true" />
              </>
            )}
          </button>

          <div
            style={{
              textAlign: "center",
              fontSize: 12,
              color: "#777",
              marginTop: 18,
            }}
          >
            Already have an account?{" "}
            <span
              onClick={() => { onSignInClick?.(); window.location.hash = "#/signin"; }}
              role="button"
              tabIndex={0}
              style={{ color: GOLD, fontWeight: 700, cursor: "pointer" }}
            >
              Sign in
            </span>
          </div>
        </form>
      </div>
    </div>
  );
}

const labelStyle = {
  display: "block",
  fontSize: 11,
  fontWeight: 700,
  color: "#888",
  textTransform: "uppercase",
  letterSpacing: 1,
  marginBottom: 6,
};

const inputStyle = {
  width: "100%",
  background: "rgba(255,255,255,0.04)",
  border: "1px solid rgba(255,255,255,0.08)",
  borderRadius: 10,
  padding: "13px 14px",
  fontSize: 14,
  color: "#fff",
  outline: "none",
  fontFamily: "inherit",
  boxSizing: "border-box",
  transition: "all .15s ease",
};
